import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { month, useCurrentPartner, useMe } from '../../lib'
import { readCookie } from '../../lib/api'
import { ApiError } from '../../lib'
import { Empty, Loading } from '../../app/ui'

export type Counts = {
  new: number; same: number; revised: number; conflict: number; invalid: number
  unmatched: number; pre_join: number; overlap_review: number; skipped: number; rows: number; duplicate_of?: string
  customers?: number; total_cents?: number; first_month?: string | null; last_month?: string | null
  // a column we need is missing: one question and the file's own columns to pick from
  missing?: 'who' | 'amount' | 'date'; question?: string; headers?: string[]; samples?: Record<string, string[]>
}

export type Batch = {
  id: string; partner_id: string; channel: 'upload' | 'email'; filename: string | null; sender: string | null
  size: number | null; period_start: string | null; period_end: string | null; complete_for_period: boolean
  status: 'received' | 'previewed' | 'approved' | 'committed' | 'rejected' | 'failed'
  sha256?: string | null
  preview: Counts | null; preview_hash: string | null
  needs_approval_reason: 'first_file' | 'errors' | 'manual' | 'changed' | 'missing_column' | null
  error: string | null; received_at: string; approved_at: string | null; committed_at: string | null
}

export type Source = {
  id: string; kind: 'stripe_key' | 'stripe_app' | 'upload' | 'email'; status: 'setup' | 'active' | 'error' | 'off'
  is_primary: boolean; last_event_at: string | null; last_error: string | null
  connection_id: string | null; stripe_account_id: string | null; livemode: boolean | null
  last_sync_at: string | null; webhook_on: boolean | null; webhook_url: string | null
}

/** Why rows in a file will not count, in a few words. */
export const RESULT_LABEL: Record<string, string> = {
  new: 'New', same: 'Already sent', revised: 'Updated', conflict: 'Clashes with what you sent before',
  invalid: 'Has a problem', unmatched: 'Customer not signed up yet', pre_join: 'Before the customer joined us',
  overlap_review: 'Also sent another way; we check it', skipped: 'Skipped',
}

export const SOURCE_LABEL: Record<Source['kind'], string> = {
  stripe_key: 'Stripe', stripe_app: 'Stripe', upload: 'Files', email: 'Email',
}

/** "Aug–Sep 2026", "Dec 2026–Jan 2027", "Sep 2026". */
export function months(from: string | null | undefined, to: string | null | undefined): string {
  if (!from) return ''
  if (!to || to.slice(0, 7) === from.slice(0, 7)) return month(from)
  const [a, b] = [month(from), month(to)]
  return a.slice(-4) === b.slice(-4) ? `${a.slice(0, -5)}–${b}` : `${a}–${b}`
}

/** Partner spend pages: needs a signed-in partner member; viewers read only. */
export function SpendPage({ children }: { children: (pid: string, canEdit: boolean) => ReactNode }) {
  const { me, isLoading } = useMe()
  const partner = useCurrentPartner()
  if (isLoading) return <Loading />
  if (!partner && !me?.user) return <Empty>Sign in to send us spend.</Empty>
  if (!partner) {
    return (
      <Empty>
        You are not on a partner team yet. <Link to="/partner/new">Sign up as a partner</Link> or ask your team's owner to invite you.
      </Empty>
    )
  }
  return (
    <div className="stack">
      {children(partner.id, partner.role !== 'viewer')}
    </div>
  )
}

/** Raw file upload (not JSON): same CSRF header the shell's api() sends. */
export async function putFile(url: string, file: File): Promise<Batch> {
  const headers: Record<string, string> = { 'Content-Type': file.type || 'application/octet-stream', Accept: 'application/json' }
  const csrf = readCookie('pr_csrf')
  if (csrf) headers['X-PR-CSRF'] = csrf
  const res = await fetch(url, { method: 'PUT', headers, credentials: 'same-origin', body: file })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    // A non-JSON failure (proxy, timeout) must still read as a sentence, never a bare status line.
    throw new ApiError(res.status, data.error ?? `http_${res.status}`,
      data.message ?? 'We could not upload your file. Please try again; if this keeps happening, contact us.')
  }
  return data as Batch
}

export function statusText(b: Batch): string {
  switch (b.status) {
    case 'received': return b.sha256 === null ? 'Uploading' : 'Reading your file'
    case 'previewed': return b.needs_approval_reason === 'missing_column' ? 'Needs one answer' : 'Ready to check'
    case 'approved': return 'Adding'
    case 'committed': return 'Added'
    case 'rejected': return 'Not added'
    case 'failed': return 'Upload failed'
  }
}

export function fileLink(id: string): string {
  return `/partner/spend/files/${id}`
}
